import React, {PureComponent, ReactNode} from 'react';
import {SimpleArtifactImage} from 'app/components/my_profile/artifacts/image';
import I18n from 'app/i18n';
import {stringTypeOfBonus} from 'app/utils/stringTypeOfBonus';
import SpriteIcon from 'app/utils/sprites/SpriteIcon';
import {lootboxPrizeTitle} from 'app/utils/lootboxPrizeTitle';
import {lootboxPrizeType, hideCalculatedPrice} from 'app/utils/lootboxPrizeType';
import {formatDropChance, formatLootboxMoney, formatPrizePrice, lootRarity} from 'app/utils/lootboxRarity';
import {fixed2int} from 'app/utils/fixed';
import {artifactToLoot, type ArtifactLike} from 'app/utils/artifactToLoot';
import HeroSlotCount from 'app/utils/hero/hero_slot_count';
import LootInterface from 'app/interfaces/LootInterface';

const DIGITAL_ICON_SIZE = 85;
const FROZEN_BADGE_ICON_SIZE = 18;

export interface PrizeCardProps {
    loot?: LootInterface | null
    artifact?: ArtifactLike | null
    empty?: boolean
    extra?: ReactNode
    hideMeta?: boolean
    hideName?: boolean
    id?: string
    onClick?: () => void
    quantity?: number
    showQuantityOrange?: boolean
    unavailable?: boolean
}

export default class PrizeCard extends PureComponent<PrizeCardProps> {
    resolvedLoot(): LootInterface | null {
        const {artifact, loot} = this.props;

        if (loot) {
            return loot;
        }

        return artifact ? artifactToLoot(artifact) : null;
    }

    rarity(): string {
        const loot = this.resolvedLoot();

        return loot ? lootRarity(loot) : 'common';
    }

    wrapperClassName(): string {
        const {empty, onClick, unavailable} = this.props;
        const rarity = this.rarity();

        return [
            'prize-card',
            `prize-card--${empty || !this.resolvedLoot() ? 'empty' : rarity}`,
            unavailable ? 'prize-card--unavailable hero__slot--unavailable' : '',
            onClick ? 'prize-card--clickable' : ''
        ].filter(Boolean).join(' ');
    }

    frameClassName(): string {
        const {empty, unavailable} = this.props;
        const rarity = this.rarity();

        return [
            'prize-card__frame',
            rarity === 'common' || empty ? '' : `prize-card__frame--${rarity}`,
            empty || !this.resolvedLoot() ? 'prize-card__frame--empty' : '',
            unavailable ? 'prize-card__frame--unavailable' : ''
        ].filter(Boolean).join(' ');
    }

    renderImage(loot: LootInterface): ReactNode {
        switch (lootboxPrizeType(loot)) {
        case 'points':
            return this.renderDigital(String(loot.points_amount), I18n.t('points', {count: loot.points_amount}));
        case 'money':
            return this.renderDigital(formatLootboxMoney(loot.money_amount));
        case 'frozen_money':
            return this.renderDigital(
                fixed2int(loot.frozen_money_amount || 0),
                I18n.t('frozen_balance'),
                'frozen'
            );
        case 'bonus':
            return this.renderDigital(
                formatLootboxMoney(loot.bonus_amount),
                stringTypeOfBonus(Boolean(loot.bonus))
            );
        default: {
            const artifact = loot.recipe || loot.steam_skin;

            return artifact ? <SimpleArtifactImage artifact={artifact}/> : null;
        }
        }
    }

    renderDigital(amount: string, measure?: string, variant?: 'frozen'): ReactNode {
        const isFrozen = variant === 'frozen';
        const boxClass = `bonus-box prize-card__digital${isFrozen ? ' bonus-box--frozen' : ''}`;

        return <div className={boxClass}>
            <SpriteIcon
                name="#svg-icon-bonus"
                className={`bonus-icon${isFrozen ? ' bonus-icon--frozen' : ''}`}
                size={{height: DIGITAL_ICON_SIZE, width: DIGITAL_ICON_SIZE}}
                fill="#333437"
            />
            <div className="bonus-description">
                <span className="bonus-preview__amount">
                    {isFrozen && <SpriteIcon
                        name="#svg-balance-frozen"
                        className="bonus-preview__frozen-icon"
                        size={{height: FROZEN_BADGE_ICON_SIZE, width: FROZEN_BADGE_ICON_SIZE}}
                    />}{amount}
                </span>
                {measure && !isFrozen && <span className="bonus-preview__measure">{measure}</span>}
            </div>
        </div>;
    }

    renderQuantity(): ReactNode {
        const {quantity, showQuantityOrange} = this.props;

        return <HeroSlotCount
            quantity={quantity}
            showHeroSlotCountOrange={showQuantityOrange}
        />;
    }

    render(): ReactNode {
        const {empty, extra, hideMeta, hideName, id, onClick} = this.props;
        const loot = this.resolvedLoot();
        const chance = loot && !hideMeta ? formatDropChance(loot.chance_percent) : null;
        const price = loot && !hideMeta && !hideCalculatedPrice(loot) ? formatPrizePrice(loot.calculated_price) : null;

        return <div className={this.wrapperClassName()} id={id} onClick={onClick}>
            <div className={this.frameClassName()}>
                {extra}
                {loot && !empty && <div className="prize-card__image hero__slot--img">
                    {this.renderImage(loot)}
                </div>}
                {chance && <span className="prize-card__chance">{chance}</span>}
                {price && <span className="prize-card__price">{price}</span>}
                {this.renderQuantity()}
            </div>
            {!hideName && loot && !empty && <div className="prize-card__name">
                {lootboxPrizeTitle(loot)}
            </div>}
        </div>;
    }
}
